import { useLayoutEffect, useRef } from 'react'

import UIIcon, { UIIconRef, UIIconType } from '@/components/UI/UIIcon'
import { useGSAP } from '@/composables/useGSAP'

type LoadingStepProps = {
  index: number
}

const values = [20, 30, 80, 100]

const iconClassNames = [
  'text-mid-blue text-[22vw]',
  'text-mid-blue text-[18vw]',
  'text-mid-blue text-[25vw]',
  'text-mid-blue text-[32vw]',
]
const icons = ['circle', 'background2', 'background1', 'background3']

const texts = ['text-[56px]', 'text-[80px]', 'text-[108px]', 'text-[122px]']

export default function LoadingStep({ index }: LoadingStepProps) {
  const { gsap } = useGSAP()

  const backgroundRef = useRef<UIIconRef>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const textWrapperRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap
        .timeline()
        .fromTo(
          backgroundRef.current,
          {
            scale: 0,
            rotate: index % 2 === 0 ? 90 : -90,
          },
          {
            scale: 1,
            rotate: 0,
            duration: 0.5,
            ease: 'back.out(1.7)',
          },
          0
        )
        .fromTo(
          textRef.current,
          {
            yPercent: 110,
            rotate: index % 2 === 0 ? 15 : -15,
            transformOrigin: index % 2 === 0 ? '0% 50%' : '100% 50%',
          },
          {
            yPercent: 0,
            rotate: 0,
            duration: 0.3,
            ease: 'back.out(1.2)',
          },
          0.2
        )
        .fromTo(
          textWrapperRef.current,
          {
            rotate: index % 2 === 0 ? 15 : -15,
          },
          {
            rotate: index % 2 === 0 ? -3 : 3,
            duration: 0.3,
            ease: 'back.out(1.2)',
          },
          0.2
        )
    })

    return () => ctx.kill()
  }, [index, gsap])

  return (
    <div className='absolute top-0 left-0 w-full h-full grid content-center justify-center'>
      <UIIcon
        ref={backgroundRef}
        icon={icons[index % icons.length] as UIIconType}
        className={`${iconClassNames[index % iconClassNames.length]} col-start-1 col-span-1 row-start-1 row-span-1 w-full md:w-auto max-md:h-auto`}
      />
      <div
        className={`select-none relative col-start-1 col-span-1 row-start-1 row-span-1 flex items-center justify-center text-dark-cream font-bold ${texts[index % texts.length]}`}
      >
        <div ref={textWrapperRef} className='relative overflow-hidden'>
          <div ref={textRef} className='relative'>
            {values[index % values.length]}
          </div>
        </div>
      </div>
    </div>
  )
}
